import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Pricing from './Pricing';
import CallToAction from './CallToAction';

const RealEstate = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Real Estate Video Marketing - Sales Intercept</title>
        <meta name="description" content="Boost real estate leads and sales with video. Add engaging property walkthroughs and agent intros to your website in minutes. Convert more browsers into buyers." />
      </Helmet>
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 lg:pt-40 pb-12 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              Turn More Browsers Into <span className="text-purple-si">Buyers</span>
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Add video to your real estate website in 5 minutes — no devs, no plugins, no hassle.
              You already know video sells homes. Now let it sell you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://dashboard.salesintercept.com/signup" 
                 className="btn btn-primary">
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">📱</div>
              <h3 className="text-xl font-bold mb-2">Record Right From Your Phone</h3>
              <p className="text-gray-600">Use your existing content or shoot a quick intro on the go. Perfect for property walkthroughs and market updates.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">🔁</div>
              <h3 className="text-xl font-bold mb-2">Repurpose Your Content</h3>
              <p className="text-gray-600">Turn your TikTok, Instagram, or YouTube clips into website widgets that work 24/7 to engage visitors.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">🔌</div>
              <h3 className="text-xl font-bold mb-2">Install in Minutes</h3>
              <p className="text-gray-600">Add to any real estate website with a simple copy/paste code. No technical expertise needed.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">💬</div>
              <h3 className="text-xl font-bold mb-2">Capture More Leads</h3>
              <p className="text-gray-600">Connect directly to Calendly, contact forms, or chat. Turn views into showings automatically.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">🎥</div>
              <h3 className="text-xl font-bold mb-2">Humanize Your Brand</h3>
              <p className="text-gray-600">Build instant trust with buyers and sellers. Stand out in your market with personal video messages.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">💼</div>
              <h3 className="text-xl font-bold mb-2">Perfect for Solo Agents</h3>
              <p className="text-gray-600">No IT team or fancy tools required. Manage everything yourself in minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="features" className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Video That <span className="text-purple-si">Works For You</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">Property Listings</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Add walkthrough videos to listings
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Highlight key features personally
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Share neighborhood insights
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">Agent Profile</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Introduce yourself to new visitors
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Share your expertise and success stories
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Build trust before the first call
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Reuse existing pricing and FAQ sections */}
      <Pricing />
      <CallToAction />
    </div>
    </>
  );
};

export default RealEstate;